import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useAvailableAssignedBuyersForRequest,
  useAvailableCategoriesForRequest,
  useAvailableTagsForRequest,
  useFilterRequestItems,
  useSearchRequestItems,
} from "../../../hooks/usePurchaseRequestsV2";
import {
  PurchaseRequestItemState,
  PurchaseRequestItemV2,
  PurchaseRequestV2,
} from "../../../types";
import i18next from "../../../i18n";

export interface FilterOption {
  id: string;
  label: string;
  color?: string;
}

export interface DetailItemsData {
  activeItems: PurchaseRequestItemV2[];
  isItemsLoading: boolean;
  hasAnyFilter: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (value: string[]) => void;
  selectedCategoryIds: string[];
  setSelectedCategoryIds: (value: string[]) => void;
  selectedBuyerIds: string[];
  setSelectedBuyerIds: (value: string[]) => void;
  selectedStates: string[];
  setSelectedStates: (value: string[]) => void;
  selectedGroups: string[];
  setSelectedGroups: (value: string[]) => void;
  clearAllFilters: () => void;
  tagOptions: FilterOption[];
  categoryOptions: FilterOption[];
  buyerOptions: FilterOption[];
  stateOptions: FilterOption[];
  /** The request's groups in file order — empty unless it came from a file of several requisitions. */
  groupOptions: FilterOption[];
  isLoadingTags: boolean;
  isLoadingCategories: boolean;
  isLoadingBuyers: boolean;
}

/**
 * The detail item table's data brain, extracted from the pre-Carbon `PurchaseRequestV2Detail`
 * (Task 5). Owns the URL-param filter/search state (tags/categories/assigned-buyers/states/`q`),
 * fetches the available-filter facets and the filtered/searched item lists, and derives
 * `activeItems` — the search-or-filter-or-unfiltered precedence the pre-Carbon screen used, still
 * excluding soft-deleted items. The available-assigned-buyers facet is read from the now-lean
 * `UserSummaryDTO` defensively (firstName/lastName/email/username fallbacks), unchanged.
 */
export const useDetailItems = (
  id: string | undefined,
  request: PurchaseRequestV2 | undefined
): DetailItemsData => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTagIds = useMemo(
    () => searchParams.getAll("tagIds"),
    [searchParams]
  );
  const selectedCategoryIds = useMemo(
    () => searchParams.getAll("categoryIds"),
    [searchParams]
  );
  const selectedBuyerIds = useMemo(
    () => searchParams.getAll("assignedBuyerIds"),
    [searchParams]
  );
  const selectedStates = useMemo(
    () => searchParams.getAll("states"),
    [searchParams]
  );
  const selectedGroups = useMemo(
    () => searchParams.getAll("groups"),
    [searchParams]
  );
  const searchQuery = searchParams.get("q") ?? "";

  const updateMultiParam = (key: string, values: string[]) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    values.forEach((v) => next.append(key, v));
    setSearchParams(next, { replace: true });
  };

  const setSelectedTagIds = (v: string[]) => updateMultiParam("tagIds", v);
  const setSelectedCategoryIds = (v: string[]) =>
    updateMultiParam("categoryIds", v);
  const setSelectedBuyerIds = (v: string[]) =>
    updateMultiParam("assignedBuyerIds", v);
  const setSelectedStates = (v: string[]) => updateMultiParam("states", v);
  const setSelectedGroups = (v: string[]) => updateMultiParam("groups", v);

  const setSearchQuery = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("q", v);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  // Groups filter on the client: they are a label on the item, and the request is already loaded.
  const hasServerFilter =
    selectedTagIds.length > 0 ||
    selectedCategoryIds.length > 0 ||
    selectedBuyerIds.length > 0 ||
    selectedStates.length > 0;
  const hasAnyFilter = hasServerFilter || selectedGroups.length > 0;

  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("tagIds");
    next.delete("categoryIds");
    next.delete("assignedBuyerIds");
    next.delete("states");
    next.delete("groups");
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const { data: availableTags, isLoading: isLoadingTags } =
    useAvailableTagsForRequest(id);
  const { data: availableCategories, isLoading: isLoadingCategories } =
    useAvailableCategoriesForRequest(id);
  const { data: availableBuyers, isLoading: isLoadingBuyers } =
    useAvailableAssignedBuyersForRequest(id);

  const { data: filteredItems, isFetching: isFilterFetching } =
    useFilterRequestItems(id, {
      tagIds: selectedTagIds,
      categoryIds: selectedCategoryIds,
      assignedBuyerIds: selectedBuyerIds,
      states: selectedStates,
    });
  const { data: searchResults, isFetching: isSearchFetching } =
    useSearchRequestItems(id, searchQuery);

  const isItemsLoading =
    (hasServerFilter && !filteredItems && isFilterFetching) ||
    (searchQuery.trim().length > 0 && !searchResults && isSearchFetching);

  const tagOptions = useMemo(
    () =>
      (availableTags ?? []).map((tag: any) => ({
        id: tag.id,
        label: tag.name,
        color: tag.color,
      })),
    [availableTags]
  );
  const categoryOptions = useMemo(
    () =>
      (availableCategories ?? []).map((category: any) => ({
        id: category.id,
        label: category.name,
      })),
    [availableCategories]
  );
  const buyerOptions = useMemo(
    () =>
      (availableBuyers ?? []).map((buyer: any) => ({
        id: buyer.id,
        label:
          [buyer.firstName, buyer.lastName].filter(Boolean).join(" ") ||
          buyer.email ||
          buyer.username ||
          String(buyer.id),
      })),
    [availableBuyers]
  );

  const stateOptions = useMemo(
    () =>
      Object.values(PurchaseRequestItemState).map((state) => ({
        id: state,
        label: i18next.t(`purchaseRequests.status.${state.toLowerCase()}`, {
          defaultValue: state,
        }),
      })),
    []
  );

  const groupOptions = useMemo(() => {
    const labels = (request?.items ?? [])
      .filter((item) => !item.deleted && item.groupLabel)
      .map((item) => item.groupLabel as string);
    return Array.from(new Set(labels)).map((label) => ({
      id: label,
      label,
    }));
  }, [request?.items]);

  const activeItems = useMemo(() => {
    const listed = (() => {
      if (searchQuery.trim().length > 0 && searchResults) return searchResults;
      if (hasServerFilter && filteredItems) return filteredItems;
      return request?.items ?? [];
    })().filter((item) => !item.deleted);
    if (selectedGroups.length === 0) return listed;
    return listed.filter(
      (item) => !!item.groupLabel && selectedGroups.includes(item.groupLabel)
    );
  }, [
    searchQuery,
    searchResults,
    hasServerFilter,
    filteredItems,
    request?.items,
    selectedGroups,
  ]);

  return {
    activeItems,
    isItemsLoading,
    hasAnyFilter,
    searchQuery,
    setSearchQuery,
    selectedTagIds,
    setSelectedTagIds,
    selectedCategoryIds,
    setSelectedCategoryIds,
    selectedBuyerIds,
    setSelectedBuyerIds,
    selectedStates,
    setSelectedStates,
    selectedGroups,
    setSelectedGroups,
    clearAllFilters,
    tagOptions,
    categoryOptions,
    buyerOptions,
    stateOptions,
    groupOptions,
    isLoadingTags,
    isLoadingCategories,
    isLoadingBuyers,
  };
};
